import { useNavigate, useParams } from 'react-router-dom'
import Button from '../../../components/common/Button'
import MainButton from '../../../components/admin/MainButton'
import { useEffect, useState } from 'react'
import useError from '../../../common/hooks/useError'
import useToast from '../../../common/hooks/useToast'
import useHistoryState from '../../../common/hooks/useHistoryState'
import useData from '../../../common/hooks/useData'
import TextInput from '../../../components/common/data-form/TextInput'
import fetchAPI from '../../../common/fetchAPI'
import AddItemsFieldSet from '../../../components/common/data-form/AddItemsFieldSet'
import { moveFieldGroup, syncFieldGroupNames } from '../../../common/helpers'
import useModal from '../../../common/hooks/useModal'
import ContactFormPreview from './ContactFormPreview'
import Spinner from '../../../components/common/Spinner'
import ErrorMessage from '../../../components/common/ErrorMessage'
import CheckboxInput from '../../../components/common/data-form/CheckboxInput'
import SectionContainer from '../../../components/common/SectionContainer'
import SwitchInput from '../../../components/common/data-form/SwitchInput'
import Toggle from '../../../components/common/Toggle'

const initFields = [
    {
        fieldType: 'text',
        label: 'My first field',
    },
]

const embedTypeOptions = [
    { value: 'HTML', label: 'HTML' },
    { value: 'JS', label: 'JS' },
]

export default function EditContactForm() {
    const { contactFormId } = useParams()
    const [formData, setFormData] = useState({
        title: '',
        whitelistedDomains: '',
        isSaveContact: false,
        embedType: 'HTML',
    })
    const [fields, setFields, fieldsHistory] = useHistoryState([], {
        groupWithinMs: 1000,
    })
    const data = { ...formData, fields }
    const [error, setError, errorFields] = useError()
    const [loading, setLoading] = useState()
    const setToast = useToast()
    const navigate = useNavigate()
    const { setModal } = useModal()

    const { formLoading, formError } = useData(
        `/v1/contact-forms/${contactFormId}`,
        'form',
        undefined,
        undefined,
        undefined,
        (item) => {
            setExtraFieldSetNames(
                Array.from(
                    new Set(
                        item.fields
                            .filter((f) => f.groupName)
                            .map((f) => f.groupName),
                    ),
                ),
            )
            setData({
                whitelistedDomains: item.whitelistedDomains.join(','),
                recaptchaSiteKey: item.recaptchaSiteKey,
                recaptchaSecretKey: item.recaptchaSecretKey,
                title: item.title,
                isSaveContact: item.isSaveContact,
                embedType: item.embedType || 'HTML',
                fields: item.fields.map((f) => ({
                    label: f.label,
                    fieldType: f.fieldType,
                    required: f.required,
                    options: f.options ? f.options.join(',') : '',
                    placeholder: f.placeholder,
                    groupName: f.groupName,
                    groupType: f.groupType,
                    isAppUserField: f.isAppUserField,
                })),
            })
            fieldsHistory.reset()
        },
    )

    const [extraFieldSetNames, setExtraFieldSetNames] = useState([])
    const [newExtraFieldSetName, setNewExtraFieldSetName] = useState('')

    // Keep field groups in sync with the fields (e.g. after undo/redo)
    useEffect(
        function () {
            setExtraFieldSetNames((prev) => syncFieldGroupNames(prev, fields))
        },
        [fields],
    )

    function setData(next) {
        const { fields: nextFields, ...nextFormData } =
            typeof next === 'function' ? next(data) : next
        setFormData(nextFormData)
        if (nextFields !== fields) {
            setFields(nextFields)
        }
    }

    function handleExtraFieldSetAdd() {
        setExtraFieldSetNames([...extraFieldSetNames, newExtraFieldSetName])
        setNewExtraFieldSetName('')

        const newFieldData = initFields.map((f) => ({
            ...f,
            groupName: newExtraFieldSetName,
            ...(data.isSaveContact ? { groupType: 'step' } : {}),
        }))
        setData({
            ...data,
            fields: [...data.fields, ...newFieldData],
        })
    }

    function handleExtraFieldSetRemoval(name) {
        setExtraFieldSetNames(extraFieldSetNames.filter((n) => n !== name))
        setData({
            ...data,
            fields: data.fields.filter((field) => field.groupName !== name),
        })
    }

    function handleGroupTypeChange(name, isStep) {
        setData({
            ...data,
            fields: data.fields.map((f) => {
                if (f.groupName !== name) {
                    return f
                }
                return { ...f, groupType: isStep ? 'step' : 'repeatable' }
            }),
        })
    }

    function handleGroupMove(name, direction) {
        setData({
            ...data,
            fields: moveFieldGroup(data.fields, name, direction),
        })
    }

    function handleMoveFieldSetUp(index) {
        const updatedFields = [...data.fields]
        ;[updatedFields[index - 1], updatedFields[index]] = [
            updatedFields[index],
            updatedFields[index - 1],
        ]
        setData({ ...data, fields: updatedFields })
    }

    function handleMoveFieldSetDown(index) {
        const updatedFields = [...data.fields]
        ;[updatedFields[index], updatedFields[index + 1]] = [
            updatedFields[index + 1],
            updatedFields[index],
        ]
        setData({ ...data, fields: updatedFields })
    }

    function handlePreview() {
        setModal(
            <ContactFormPreview data={data} />,
            "Preview: Appearance will depend on your site's CSS",
        )
    }

    async function handleSubmit(e) {
        setError('')
        e.preventDefault()

        if (!data.fields.length) {
            setError('You need to add at least one field.')
            return
        }

        setLoading(true)

        const { error, meta } = await fetchAPI(
            `/v1/contact-forms/${contactFormId}`,
            {
                ...data,
                isSaveContact: data.isSaveContact,
                whitelistedDomains: data.whitelistedDomains.split(','),
                fields: data.fields.map((field) => ({
                    ...field,
                    options:
                        field.fieldType === 'select'
                            ? Array.isArray(field.options)
                                ? field.options
                                : (field.options || '').split(',')
                            : undefined,
                })),
            },
            'PATCH',
        )
        setLoading(false)

        if (error) {
            setError(error, meta?.fields)
            return
        }

        setToast('Saved. You may need to copy a new embed code.')
        navigate(`/contact-forms/${contactFormId}`)
    }

    if (formLoading) {
        return <Spinner />
    }

    if (formError) {
        return (
            <SectionContainer>
                <ErrorMessage>{formError}</ErrorMessage>
            </SectionContainer>
        )
    }

    const hasFieldSets = data.fields.some((f) => f.groupName)
    if (hasFieldSets && !extraFieldSetNames.length) {
        return null
    }

    const isSettingsForcedOpen =
        !data.title ||
        !data.whitelistedDomains ||
        errorFields.some((f) => !f.startsWith('fields_'))

    return (
        <SectionContainer>
            {error && (
                <ErrorMessage onDismiss={() => setError('')}>
                    {error}
                </ErrorMessage>
            )}

            <form onSubmit={handleSubmit}>
                <Toggle
                    title="Form settings"
                    preventParamsUpdate
                    open={isSettingsForcedOpen}
                >
                    <label htmlFor={'form-title'} className="required">
                        Title
                    </label>
                    <TextInput
                        id={'form-title'}
                        value={data.title}
                        onChange={(v) =>
                            setData({
                                ...data,
                                title: v,
                            })
                        }
                        required
                        errors={errorFields.filter((f) => f === 'title')}
                    />

                    <label htmlFor={'form-domains'} className="required">
                        Whitelisted domains
                    </label>
                    <small className="input-info">
                        Separate domains with a comma. The contact form
                        won&apos;t accept submissions from other domains.
                    </small>
                    <TextInput
                        id={'form-domains'}
                        value={data.whitelistedDomains}
                        placeholder={'example.com,www.example.org'}
                        onChange={(v) =>
                            setData({
                                ...data,
                                whitelistedDomains: v,
                            })
                        }
                        required
                        errors={errorFields.filter((f) =>
                            f.startsWith('whitelistedDomains'),
                        )}
                    />

                    <label htmlFor={'form-recaptcha'}>
                        ReCAPTCHA v2 Checkbox Site Key
                    </label>
                    <small className="input-info">
                        <a
                            href="https://www.google.com/recaptcha/admin/create"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Create one here
                        </a>{' '}
                        and add it to enable ReCAPTCHA.
                    </small>
                    <TextInput
                        id={'form-recaptcha'}
                        value={data.recaptchaSiteKey}
                        onChange={(v) =>
                            setData({
                                ...data,
                                recaptchaSiteKey: v,
                            })
                        }
                        errors={errorFields.filter(
                            (f) => f === 'recaptchaSiteKey',
                        )}
                    />

                    <label htmlFor={'form-recaptcha-secret'}>
                        ReCAPTCHA v2 Checkbox Secret Key
                    </label>
                    <TextInput
                        id={'form-recaptcha-secret'}
                        value={data.recaptchaSecretKey}
                        onChange={(v) =>
                            setData({
                                ...data,
                                recaptchaSecretKey: v,
                            })
                        }
                        errors={errorFields.filter(
                            (f) => f === 'recaptchaSecretKey',
                        )}
                    />

                    <label htmlFor={'form-embed-type'}>Embed type</label>
                    <small className="input-info">
                        HTML embeds the full form code. JS embeds a small script
                        that loads the latest form, so you won&apos;t need to
                        copy a new embed code after edits.
                    </small>
                    <SwitchInput
                        id={'form-embed-type'}
                        value={data.embedType}
                        options={embedTypeOptions}
                        onChange={(v) =>
                            setData((prev) => ({
                                ...prev,
                                embedType: v,
                            }))
                        }
                        errors={errorFields.filter((f) => f === 'embedType')}
                    />

                    <CheckboxInput
                        id="form-save-lead"
                        label="Save submissions as contacts"
                        value={data.isSaveContact}
                        onChange={(v) =>
                            setData({
                                ...data,
                                isSaveContact: v,
                            })
                        }
                        info={
                            'Save the form submission as a contact in the CRM. Only steps are allowed, not repeatable field groups. Requires at least a first name and last name field.'
                        }
                    />
                </Toggle>
                <br />

                <AddItemsFieldSet
                    data={data}
                    setData={setData}
                    history={fieldsHistory}
                    errors={errorFields.filter((f) => f.startsWith(`fields_`))}
                    moveFieldSetUp={(index) =>
                        index > 0 && handleMoveFieldSetUp(index)
                    }
                    moveFieldSetDown={(index) =>
                        index < data.fields.length - 1 &&
                        handleMoveFieldSetDown(index)
                    }
                />

                {extraFieldSetNames.map((name) => (
                    <div key={name}>
                        <AddItemsFieldSet
                            title={name}
                            data={data}
                            setData={setData}
                            history={fieldsHistory}
                            deleteFieldSet={() =>
                                handleExtraFieldSetRemoval(name)
                            }
                            errors={errorFields.filter((f) =>
                                f.startsWith(`fields_`),
                            )}
                            moveFieldSetUp={() => handleGroupMove(name, -1)}
                            moveFieldSetDown={() => handleGroupMove(name, 1)}
                            isStep={
                                data.fields.find((f) => f.groupName === name)
                                    ?.groupType === 'step'
                            }
                            onGroupTypeChange={(isStep) =>
                                handleGroupTypeChange(name, isStep)
                            }
                        />
                    </div>
                ))}

                <label htmlFor="form-next-set">New field group</label>
                <TextInput
                    id={'form-next-set'}
                    value={newExtraFieldSetName}
                    onChange={(v) => setNewExtraFieldSetName(v)}
                />
                <Button
                    text="Add field group"
                    outline
                    disabled={!newExtraFieldSetName}
                    onClick={handleExtraFieldSetAdd}
                />

                <Button
                    text="Preview"
                    isLoading={loading}
                    outline
                    type={'button'}
                    onClick={handlePreview}
                />

                <Button text="Save" isLoading={loading} type={'submit'} />

                <MainButton
                    disabled={loading}
                    functionality="SAVE"
                    loading={loading}
                />
            </form>
        </SectionContainer>
    )
}
